import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { getCompanyData } from "../service/client";
import { getCookieConsent } from "../utils/cookieConsent";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const GTM_RESTAURANTS_ID = "GTM-KDZCK397"; // wszystkie strony flex_menu_app

function injectGTM(gtmId: string) {
  const headScript = document.createElement("script");
  headScript.setAttribute("data-gtm", gtmId);
  headScript.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
  document.head.appendChild(headScript);

  const noscript = document.createElement("noscript");
  noscript.setAttribute("data-gtm", `${gtmId}-noscript`);
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);
}

function removeGTMScripts() {
  document.querySelectorAll("[data-gtm]").forEach((el) => el.remove());
}

function injectGoogleAnalytics(gaId: string, label: string): HTMLElement[] {
  const elements: HTMLElement[] = [];

  const gtagScript = document.createElement("script");
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  gtagScript.setAttribute("data-analytics", label);
  document.head.appendChild(gtagScript);
  elements.push(gtagScript);

  const configScript = document.createElement("script");
  configScript.setAttribute("data-analytics", `${label}-config`);
  configScript.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaId}');
  `;
  document.head.appendChild(configScript);
  elements.push(configScript);

  return elements;
}

function injectFacebookPixel(pixelId: string, label: string): HTMLElement[] {
  const elements: HTMLElement[] = [];

  const pixelScript = document.createElement("script");
  pixelScript.setAttribute("data-analytics", label);
  pixelScript.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(pixelScript);
  elements.push(pixelScript);

  const noscript = document.createElement("noscript");
  noscript.setAttribute("data-analytics", `${label}-noscript`);
  noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" />`;
  document.head.appendChild(noscript);
  elements.push(noscript);

  return elements;
}

function removeAllAnalyticsScripts() {
  document.querySelectorAll("[data-analytics]").forEach((el) => el.remove());
}

export function useAnalytics() {
  const gtmInjectedRef = useRef(false);
  const analyticsInjectedRef = useRef(false);
  const location = useLocation();

  const { data: companyData } = useQuery(
    ["/company-data"],
    getCompanyData,
    { staleTime: 5 * 60 * 1000 }
  );

  // Wstrzykuj GTM raz przy pierwszym renderze
  useEffect(() => {
    if (gtmInjectedRef.current) return;

    injectGTM(GTM_RESTAURANTS_ID);
    gtmInjectedRef.current = true;

    return () => {
      removeGTMScripts();
      gtmInjectedRef.current = false;
    };
  }, []);

  // Wysyłaj pageview do dataLayer przy każdej zmianie URL (SPA navigation)
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "pageview",
      page_path: location.pathname + location.search,
    });
  }, [location.pathname, location.search]);

  // Opcjonalny GA i FB Pixel restauracji
  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent.analytics) {
      if (analyticsInjectedRef.current) {
        removeAllAnalyticsScripts();
        analyticsInjectedRef.current = false;
      }
      return;
    }

    if (analyticsInjectedRef.current) return;

    if (companyData) {
      analyticsInjectedRef.current = true;

      if (companyData.googleAnalyticsId) {
        injectGoogleAnalytics(companyData.googleAnalyticsId, "ga-restaurant");
      }

      if (companyData.facebookPixelId) {
        injectFacebookPixel(companyData.facebookPixelId, "fb-pixel-restaurant");
      }
    }
  }, [companyData]);

  // Nasłuchuj na zmiany zgody (po kliknięciu "Akceptuj" w bannerze)
  useEffect(() => {
    const handleConsentChange = () => {
      const consent = getCookieConsent();
      if (consent.analytics && !analyticsInjectedRef.current) {
        window.location.reload();
      }
    };

    window.addEventListener("cookie-consent-changed", handleConsentChange);
    return () => {
      window.removeEventListener("cookie-consent-changed", handleConsentChange);
    };
  }, []);
}
